import { Grid, TextField } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierVisitDirection, RailVisitResponseDto } from '@planning/app/api'
import { RailcarAutocomplete } from '@planning/components'
import RailTrackAutocomplete from '@planning/pages/Order/components/RailTrackAutocomplete'
import { railVisitService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment } from '@tom-ui/ui'
import {
  notAllowPlusMinusPointSign,
  notAllowPlusMinusSign,
  onlyAllowPositiveInt,
} from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { AddEmptyRailcarFormStore } from '../../stores/AddEmptyRailcarFormStore'

interface Props {
  direction: CarrierVisitDirection
  railVisit: RailVisitResponseDto
  onSuccess: () => void
}

export const AddEmptyRailcarPageForm: FC<Props> = observer(
  ({ direction, railVisit, onSuccess }) => {
    const { appViewStore, railcarTrackPositionItemStore, railVisitItemStore } = usePlanningStore()
    const { t } = useTranslate()

    const store = useMemo(() => new AddEmptyRailcarFormStore(), [])

    const railTracks = computed(() => {
      const allTracks = railcarTrackPositionItemStore.getRailTracks()
      const assignedIds = railVisitItemStore.getById(railVisit.id)?.data.assignedRailTrackIds || []
      return allTracks.filter(track => assignedIds.includes(track.id))
    }).get()

    const usedRailcarIds = computed(() => {
      const visitItem = railVisitItemStore.getById(railVisit.id)
      if (!visitItem) return []

      const allPositions = visitItem.railcarTrackPositions || []

      const ids = allPositions.map(pos => pos.data.railcarId).filter((id): id is number => !!id)

      return [...new Set(ids)]
    }).get()

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      if (!store.validate()) return

      try {
        appViewStore.showLoader()
        const cmd = store.getCreateCommand(railVisit.id, direction)
        await railVisitService.createEmptyRailcar(cmd)

        appViewStore.setShowAlert(
          'success',
          t('railcarAddedSuccessfully', 'Railcar added successfully'),
        )
        onSuccess()
      } catch (error: any) {
        const detail = error.response?.data?.detail
        if (detail) appViewStore.setShowAlert('error', detail)
      } finally {
        appViewStore.hideLoader()
      }
    }

    return (
      <form id='add-empty-railcar-form' onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RailTrackAutocomplete
              value={store.railTrackId.value}
              onChange={val => store.railTrackId.setValue(val)}
              label={t('railTrack', 'Rail track')}
              options={railTracks}
              hasError={!!store.railTrackId.error}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <RailcarAutocomplete
              required
              value={store.railcarId.value}
              onChange={val => store.railcarId.setValue(val)}
              label={t('railcarNo', 'Railcar no.')}
              hasError={!!store.railcarId.error}
              excludeIds={usedRailcarIds}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('length', 'Length')}
              variant='outlined'
              type='number'
              {...store.length.props}
              onKeyDown={notAllowPlusMinusSign}
              InputProps={{
                endAdornment: <CustomInputAdornment position='end'>m</CustomInputAdornment>,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('maxPayload', 'Max payload')}
              variant='outlined'
              type='number'
              {...store.maxPayload.props}
              onKeyDown={notAllowPlusMinusSign}
              InputProps={{
                endAdornment: <CustomInputAdornment position='end'>kg</CustomInputAdornment>,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label={t('sequenceNo', 'Sequence no.')}
              required
              variant='outlined'
              type='number'
              {...store.sequenceNumber.props}
              onKeyDown={notAllowPlusMinusPointSign}
              onChange={e => {
                onlyAllowPositiveInt(e as any)
                store.sequenceNumber.onChange(e)
              }}
              error={!!store.sequenceNumber.error}
              helperText={store.sequenceNumber.error && t('fieldIsRequired', 'Field is required.')}
            />
          </Grid>
        </Grid>
      </form>
    )
  },
)
